'use client';

import { c as _c } from "react/compiler-runtime";
import type { HofDashboardData, HofDashboardForm } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { LABEL_CLASS, Panel, ProgressPill, SegmentBar } from '@/features/hof-dashboard/components/dashboard-ui';
import { DeadlineStrip, DeadlineTimeline, dueByDay } from '@/features/hof-dashboard/components/deadline-list';
import { FormDetail } from '@/features/hof-dashboard/components/form-detail';
import { HOF_DASHBOARD_AREA_LABELS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { formatDate, translate } from '@/features/hof-dashboard/texts';
import { isOpen, type SubmissionProgress } from '@/features/hof-dashboard/utils/submission-progress';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';

/**
 * The form an area shows when none is chosen, in the editors' order: for a reviewer the first
 * one waiting for the Ressort, for the Hof the first one still open; else the first one.
 */
const defaultFormOf = (forms: readonly Pick<HofDashboardForm, 'id' | 'entries'>[], progress: Record<string, SubmissionProgress>, isReviewer: boolean): string | undefined => {
  const toReview = forms.find(form => form.entries.some(entry => entry.status === 'submitted' || entry.status === 'inReview'));
  if (isReviewer && toReview !== undefined) return toReview.id;
  const firstOpen = forms.find(form => {
    const formProgress = progress[form.id];
    return formProgress !== undefined && isOpen(formProgress);
  });
  return (firstOpen ?? forms[0])?.id;
};

/**
 * One area: how far the Hof is and the area's deadlines across the top, then its forms as a
 * list on the left and the chosen one beside it. With a single form the list is left out.
 */
export const AreaView: React.FC<{
  area: HofDashboardArea;
  data: HofDashboardData;
  progress: Record<string, SubmissionProgress>;
  selectedFormId: string | undefined;
  onSelect: (formId: string) => void;
  locale: Locale;
}> = t0 => {
  const $ = _c(41);
  const {
    area,
    data,
    progress,
    selectedFormId,
    onSelect,
    locale
  } = t0;
  let t1;
  if ($[0] !== area || $[1] !== data.deadlines || $[2] !== data.documents || $[3] !== data.forms || $[4] !== data.hof || $[5] !== data.isReviewer || $[6] !== locale || $[7] !== onSelect || $[8] !== progress || $[9] !== selectedFormId) {
    let t2;
    if ($[11] !== area) {
      t2 = form => form.area === area;
      $[11] = area;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    const forms = data.forms.filter(t2);
    let t3;
    if ($[13] !== area || $[14] !== data.deadlines) {
      let t4;
      if ($[16] !== area) {
        t4 = deadline => deadline.area === area;
        $[16] = area;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      t3 = data.deadlines.filter(t4);
      $[13] = area;
      $[14] = data.deadlines;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    const deadlines = t3;
    let t4;
    if ($[18] !== area || $[19] !== data.documents) {
      let t5;
      if ($[21] !== area) {
        t5 = document => document.area === area;
        $[21] = area;
        $[22] = t5;
      } else {
        t5 = $[22];
      }
      t4 = data.documents.filter(t5);
      $[18] = area;
      $[19] = data.documents;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const documents = t4;
    const selected = forms.some(form_0 => form_0.id === selectedFormId) ? selectedFormId : defaultFormOf(forms, progress, data.isReviewer);
    let t5;
    if ($[23] !== progress) {
      t5 = form_1 => progress[form_1.id]?.state;
      $[23] = progress;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const states = forms.map(t5);
    const done = states.filter(_temp).length;
    const count = translate("progressCount", locale, {
      done,
      total: forms.length
    });
    const label = HOF_DASHBOARD_AREA_LABELS[area][locale];
    let t6;
    if ($[25] !== progress) {
      t6 = form_2 => {
        const formProgress = progress[form_2.id];
        return {
          deadline: form_2.deadline,
          open: formProgress !== undefined && isOpen(formProgress)
        };
      };
      $[25] = progress;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    const due = dueByDay(forms.map(t6), new Date());
    const t7 = forms.length > 0 && <div className="space-y-2"><p className="text-heading text-base font-semibold whitespace-nowrap tabular-nums">{count}</p><SegmentBar label={`${label}: ${count}`} segments={states.map(_temp2)} /></div>;
    let t8;
    if ($[27] !== deadlines || $[28] !== due || $[29] !== locale) {
      t8 = <div className="hidden @min-[640px]:block"><DeadlineStrip deadlines={deadlines} due={due} locale={locale} /></div>;
      $[27] = deadlines;
      $[28] = due;
      $[29] = locale;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== deadlines || $[32] !== due || $[33] !== locale) {
      t9 = <div className="@min-[640px]:hidden">{deadlines.length === 0 ? <p className="text-sm text-green-600">{translate("noDeadlines", locale)}</p> : <DeadlineTimeline deadlines={deadlines} due={due} locale={locale} />}</div>;
      $[31] = deadlines;
      $[32] = due;
      $[33] = locale;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== t8 || $[36] !== t9) {
      t10 = <div className="min-w-0">{t8}{t9}</div>;
      $[35] = t8;
      $[36] = t9;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    let t11;
    if ($[38] !== t10 || $[39] !== t7) {
      t11 = <Panel className="grid gap-5 py-4 @min-[640px]:grid-cols-[minmax(9rem,11rem)_minmax(0,1fr)] @min-[640px]:items-center @min-[640px]:gap-8 @xl:py-4">{t7}{t10}</Panel>;
      $[38] = t10;
      $[39] = t7;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    t1 = <div className="space-y-6">{t11}{forms.length === 0 ? <Panel><p className="text-sm text-green-700">{translate("noForms", locale)}</p></Panel> : <div className={cn("grid items-start gap-6", forms.length > 1 && "@min-[760px]:grid-cols-[15rem_minmax(0,1fr)]")}>{forms.length > 1 && <Panel className="p-3 @xl:p-3"><p className={cn(LABEL_CLASS, "px-3 pt-2 pb-3")}>{translate("submissions", locale)}</p><ul className="space-y-1">{forms.map(form_3 => {
              const formProgress_0 = progress[form_3.id];
              const chosen = form_3.id === selected;
              return <li key={form_3.id}><button type="button" aria-current={chosen ? "true" : undefined} className={cn("w-full cursor-pointer space-y-2 rounded-xl border px-4 py-3 text-left transition focus-visible:outline-2 focus-visible:outline-green-600", chosen ? "bg-quiet border-conveniat-green" : "border-transparent hover:bg-gray-50")} onClick={() => onSelect(form_3.id)}><span className="block text-base font-semibold text-balance text-green-900">{form_3.title}</span>{formProgress_0 !== undefined && <span className="block"><ProgressPill progress={formProgress_0} locale={locale} /></span>}{form_3.deadline !== undefined && <span className="block text-sm text-green-700 tabular-nums">{translate("dueOn", locale, {
                      date: formatDate(form_3.deadline, locale)
                    })}</span>}</button></li>;
            })}</ul></Panel>}<div className="min-w-0">{forms.map(form_4 => {
            const formProgress_1 = progress[form_4.id];
            return formProgress_1 === undefined ? undefined : <FormDetail key={form_4.id} form={form_4} progress={formProgress_1} hofId={data.hof.id} isReviewer={data.isReviewer} documents={documents} hidden={form_4.id !== selected} locale={locale} />;
          })}</div></div>}</div>;
    $[0] = area;
    $[1] = data.deadlines;
    $[2] = data.documents;
    $[3] = data.forms;
    $[4] = data.hof;
    $[5] = data.isReviewer;
    $[6] = locale;
    $[7] = onSelect;
    $[8] = progress;
    $[9] = selectedFormId;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
function _temp(state) {
  return state === "done";
}
function _temp2(state_0) {
  return state_0 === "done" ? "filled" : "empty";
}